import { useState, useEffect, useCallback } from 'react';

interface DeviceGateResult {
    /** True if the viewport is below the minimum width and user hasn't bypassed */
    isGated: boolean;
    /** Human-readable label for the minimum device class */
    minimumLabel: 'tablet' | 'desktop';
    /** True if user manually bypassed the gate */
    isBypassed: boolean;
    /** Call to let the user proceed anyway */
    bypass: () => void;
}

/**
 * Hook to gate tool access on small viewports.
 *
 * Shows a "Best on tablet or desktop" banner when the viewport is below the
 * specified minimum width. Users can bypass the gate if needed.
 *
 * @param minWidthPx - Minimum viewport width in pixels (768 = tablet, 1024 = desktop).
 *                      Pass undefined to disable gating entirely.
 * @returns Gate state with bypass capability
 */
export function useDeviceGate(minWidthPx?: number): DeviceGateResult {
    // When no minWidth is set, the gate is always open (tool is mobile-friendly)
    const effectiveMin = minWidthPx ?? 0;

    const [meetsMinimum, setMeetsMinimum] = useState(() =>
        typeof window !== 'undefined'
            ? window.matchMedia(`(min-width: ${effectiveMin}px)`).matches
            : true
    );
    const [isBypassed, setIsBypassed] = useState(false);

    useEffect(() => {
        if (effectiveMin === 0) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setMeetsMinimum(true);
            return;
        }
        const mq = window.matchMedia(`(min-width: ${effectiveMin}px)`);
        const handler = (e: MediaQueryListEvent) => {
            setMeetsMinimum(e.matches);
            // Reset bypass if user resizes above minimum
            if (e.matches) setIsBypassed(false);
        };
        mq.addEventListener('change', handler, { passive: true });
        // Sync initial state
         
        setMeetsMinimum(mq.matches);
        return () => mq.removeEventListener('change', handler);
    }, [effectiveMin]);

    const bypass = useCallback(() => setIsBypassed(true), []);

    const minimumLabel: 'tablet' | 'desktop' = effectiveMin >= 1024 ? 'desktop' : 'tablet';

    return {
        isGated: !meetsMinimum && !isBypassed,
        minimumLabel,
        isBypassed,
        bypass,
    };
}
